import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight, History as HistoryIcon, Filter, RefreshCw, Eye } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { format, addDays, subDays, addMonths, subMonths, addYears, subYears, isAfter, startOfDay, startOfMonth, startOfYear } from "date-fns";
import axios from "axios";
import { useAuth, API } from "@/App";
import { formatDate, formatTime, formatDateObj } from "@/lib/dateTime";
import { toast } from "sonner";
import { PageHeader } from "@/components/PageHeader";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useColumnManager } from "@/hooks/useColumnManager";
import { ColumnModals } from "@/components/ColumnModals";
import {
  CUSTOMIZABLE_APPOINTMENT_COLUMN_IDS,
  getAppointmentColumns,
} from "@/lib/appointmentColumns";

// Shared 19-column set. History shows the Day's Outcome
// (appointment_day_outcome) — what actually happened.
const HISTORY_COLUMNS = getAppointmentColumns("Day's Outcome");

const HISTORY_PREF_KEY = "history_v2"; // v2 sidesteps old 6-column saved prefs

const History = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get("tab") || "date");
  const today = startOfDay(new Date());
  const yesterday = subDays(today, 1);

  const [selectedDate, setSelectedDate] = useState(yesterday);
  const [selectedMonth, setSelectedMonth] = useState(yesterday);
  const [selectedYear, setSelectedYear] = useState(yesterday);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [dateAppointments, setDateAppointments] = useState([]);
  const [monthAppointments, setMonthAppointments] = useState([]);
  const [yearAppointments, setYearAppointments] = useState([]);
  const [customRangeAppointments, setCustomRangeAppointments] = useState([]);
  const [customRangeApplied, setCustomRangeApplied] = useState(false);
  const [loading, setLoading] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  // Filter state — keys match backend query params
  const [filters, setFilters] = useState({
    source: "",
    sa: "",
    service_type: "",
    status: "",
  });
  const [settings, setSettings] = useState({});

  // Column visibility + ordering — per-user, stored under HISTORY_PREF_KEY.
  const columnManager = useColumnManager(
    HISTORY_PREF_KEY,
    HISTORY_COLUMNS,
    CUSTOMIZABLE_APPOINTMENT_COLUMN_IDS
  );
  const { visibleCols } = columnManager;

  useEffect(() => {
    if (activeTab) {
      setSearchParams({ tab: activeTab });
    }
  }, [activeTab, setSearchParams]);

  useEffect(() => {
    fetchSettings();
  }, []);

  useEffect(() => {
    if (activeTab === "date") {
      fetchDateAppointments();
    } else if (activeTab === "month") {
      fetchMonthAppointments();
    } else if (activeTab === "year") {
      fetchYearAppointments();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, selectedMonth, selectedYear, activeTab, filters]);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`, { withCredentials: true });
      setSettings(response.data);
    } catch (error) {
      console.error("Failed to load settings");
    }
  };

  // Reschedule badge / tooltip helper (mirrors DayView + Upcoming behaviour).
  const getRescheduleEntries = (history) => (history || []).filter(h => h.from_date);

  const renderHistCell = (colId, apt) => {
    switch (colId) {
      case "time":
        return <span className="text-sm font-medium">{formatTime(apt.appointment_time)}</span>;
      case "source":
        return <span className="text-sm">{apt.source || "-"}</span>;
      case "customer_name":
        return (
          <div className="flex items-center gap-1">
            <span className="text-sm font-medium">{apt.customer_name}</span>
            {apt.priority_customer && (
              <div className="w-4 h-4 bg-black text-white rounded-sm flex items-center justify-center text-[10px] font-bold shrink-0">P</div>
            )}
            {apt.is_rescheduled && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger>
                    <div className="w-4 h-4 bg-amber-100 text-amber-800 rounded-sm flex items-center justify-center text-[10px] font-bold shrink-0 cursor-help" data-testid={`reschedule-badge-${apt.appointment_id}`}>R</div>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="max-w-[240px] p-3 bg-gray-100 border-0">
                    {(() => {
                      const entries = getRescheduleEntries(apt.reschedule_history);
                      return entries.length > 0 ? (
                        <>
                          <p className="font-bold text-sm mb-2 text-black">{entries.length} reschedule{entries.length !== 1 ? "s" : ""}</p>
                          {entries.map((h, i) => (
                            <p key={i} className="text-sm text-black mb-1">&gt; {formatDate(h.from_date)}</p>
                          ))}
                        </>
                      ) : (
                        <p className="text-sm text-black">Rescheduled</p>
                      );
                    })()}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        );
      case "phone":
        return <span className="text-sm">{apt.customer_phone || "-"}</span>;
      case "mail_id":
        return <span className="text-sm">{apt.customer_email || "-"}</span>;
      case "reg_no":
        return <span className="text-sm font-medium">{apt.vehicle_reg_no || "-"}</span>;
      case "vin":
        return <span className="text-sm">{apt.vin || "-"}</span>;
      case "model":
        return <span className="text-sm">{apt.vehicle_model || apt.model || "-"}</span>;
      case "current_km":
        return <span className="text-sm">{apt.current_km || "-"}</span>;
      case "ots":
        return <span className="text-sm">{apt.ots || apt.ots_recall ? "Yes" : "No"}</span>;
      case "service_type":
        return <Badge variant="outline" className="rounded-sm text-xs font-medium whitespace-nowrap">{apt.service_type}</Badge>;
      case "sa_name":
        return <span className="text-sm">{apt.allocated_sa || "-"}</span>;
      case "docket_readiness":
        return (
          <Badge className={`rounded-sm text-xs font-medium border-0 whitespace-nowrap ${apt.docket_readiness ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}>
            {apt.docket_readiness ? "Yes" : "No"}
          </Badge>
        );
      case "n1":
        return <span className="text-xs">{apt.n_minus_1_confirmation_status || "Pending"}</span>;
      case "status":
        // History shows the Day's Outcome — what actually happened.
        return <span className="text-xs">{apt.appointment_day_outcome || "Pending"}</span>;
      case "cre_name":
        return <span className="text-sm">{apt.cre_name || "-"}</span>;
      case "lost_customer":
        return (
          <Badge className={`rounded-sm text-xs font-medium border-0 whitespace-nowrap ${apt.lost_customer ? "bg-red-100 text-red-800" : "bg-gray-100 text-gray-800"}`}>
            {apt.lost_customer ? "Yes" : "No"}
          </Badge>
        );
      case "remarks":
        return apt.specific_repair_request ? (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="text-xs font-medium cursor-help underline decoration-dotted">Yes</span>
              </TooltipTrigger>
              <TooltipContent className="max-w-xs"><p className="text-sm">{apt.specific_repair_request}</p></TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : <span className="text-xs text-gray-400">No</span>;
      case "actions":
        return (
          <Button variant="ghost" size="icon" className="h-8 w-8 rounded-sm" onClick={() => navigate(`/appointments/${apt.appointment_id}`)} data-testid={`action-btn-${apt.appointment_id}`}>
            <Eye className="w-4 h-4" strokeWidth={1.5} />
          </Button>
        );
      default:
        return null;
    }
  };

  const buildFilterParams = (params) => {
    Object.entries(filters).forEach(([key, value]) => {
      if (value) params.append(key, value);
    });
    return params;
  };

  const fetchDateAppointments = async () => {
    setLoading(true);
    try {
      const dateStr = format(selectedDate, "yyyy-MM-dd");
      const params = buildFilterParams(new URLSearchParams({ view: "day", date: dateStr }));
      const response = await axios.get(`${API}/appointments?${params}`, { withCredentials: true });
      setDateAppointments(response.data);
    } catch (error) {
      toast.error("Failed to load appointments");
    } finally {
      setLoading(false);
    }
  };

  const fetchMonthAppointments = async () => {
    setLoading(true);
    try {
      const month = selectedMonth.getMonth() + 1;
      const year = selectedMonth.getFullYear();
      const params = buildFilterParams(new URLSearchParams({ view: "month", month: month.toString(), year: year.toString() }));
      const response = await axios.get(`${API}/appointments?${params}`, { withCredentials: true });
      setMonthAppointments(response.data);
    } catch (error) {
      toast.error("Failed to load appointments");
    } finally {
      setLoading(false);
    }
  };

  const fetchYearAppointments = async () => {
    setLoading(true);
    try {
      const year = selectedYear.getFullYear();
      const params = buildFilterParams(new URLSearchParams({ view: "year", year: year.toString() }));
      const response = await axios.get(`${API}/appointments?${params}`, { withCredentials: true });
      setYearAppointments(response.data);
    } catch (error) {
      toast.error("Failed to load appointments");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === "custom" && fromDate && toDate) fetchCustomRangeAppointments();
  }, [activeTab, fromDate, toDate]);

  const fetchCustomRangeAppointments = async () => {
    if (!fromDate || !toDate) {
      toast.error("Please select both dates");
      return;
    }
    setLoading(true);
    setCustomRangeApplied(true);
    try {
      const fromStr = format(fromDate, "yyyy-MM-dd");
      const toStr = format(toDate, "yyyy-MM-dd");
      const params = buildFilterParams(new URLSearchParams({ view: "custom", date_from: fromStr, date_to: toStr }));
      const response = await axios.get(`${API}/appointments?${params}`, { withCredentials: true });
      setCustomRangeAppointments(response.data);
    } catch (error) {
      toast.error("Failed to load appointments");
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    if (activeTab === "date") fetchDateAppointments();
    else if (activeTab === "month") fetchMonthAppointments();
    else if (activeTab === "year") fetchYearAppointments();
    else if (activeTab === "custom") fetchCustomRangeAppointments();
  };

  // Date navigation — history only goes up to yesterday
  const handlePrevDate = () => setSelectedDate(subDays(selectedDate, 1));
  const handleNextDate = () => {
    const newDate = addDays(selectedDate, 1);
    if (!isAfter(newDate, yesterday)) setSelectedDate(newDate);
  };
  const canGoForwardDate = !isAfter(addDays(selectedDate, 1), yesterday);

  // Month navigation
  const handlePrevMonth = () => setSelectedMonth(subMonths(selectedMonth, 1));
  const handleNextMonth = () => {
    const newMonth = addMonths(selectedMonth, 1);
    if (!isAfter(startOfMonth(newMonth), startOfMonth(yesterday))) setSelectedMonth(newMonth);
  };
  const canGoForwardMonth = !isAfter(startOfMonth(addMonths(selectedMonth, 1)), startOfMonth(yesterday));

  // Year navigation
  const handlePrevYear = () => setSelectedYear(subYears(selectedYear, 1));
  const handleNextYear = () => {
    const newYear = addYears(selectedYear, 1);
    if (!isAfter(startOfYear(newYear), startOfYear(yesterday))) setSelectedYear(newYear);
  };
  const canGoForwardYear = !isAfter(startOfYear(addYears(selectedYear, 1)), startOfYear(yesterday));

  const updateFilter = (key, v) => {
    setFilters((prev) => ({ ...prev, [key]: v === "all" ? "" : v }));
  };

  const AppointmentTable = ({ appointments }) => (
    <Card className="border border-gray-200 rounded-sm shadow-none overflow-hidden">
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="table-header bg-gray-50">
              {visibleCols.map((col) => (
                <TableHead key={col.id} className={`text-xs font-bold uppercase whitespace-nowrap text-center${col.id === "actions" ? " w-12" : ""}`}>{col.label}</TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {appointments.length === 0 ? (
              <TableRow>
                <TableCell colSpan={visibleCols.length} className="text-center text-gray-400 py-8">
                  No appointments found
                </TableCell>
              </TableRow>
            ) : (
              appointments.map((apt) => (
                <TableRow key={apt.appointment_id} className="hover:bg-gray-50">
                  {visibleCols.map((col) => (
                    <TableCell key={col.id} className={`whitespace-nowrap text-center${col.id === "customer_name" ? " text-left" : ""}`}>
                      {renderHistCell(col.id, apt)}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </Card>
  );

  const FilterDialog = () => (
    <Dialog open={filterOpen} onOpenChange={setFilterOpen}>
      <DialogContent className="rounded-sm max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-heading font-bold text-xl tracking-tight uppercase">
            Filters
          </DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-4 pt-4">
          <div>
            <Label className="text-[10px] font-bold uppercase tracking-widest text-gray-500 mb-1.5 block">
              Source
            </Label>
            <Select
              value={filters.source || "all"}
              onValueChange={(v) => updateFilter("source", v)}
            >
              <SelectTrigger className="rounded-sm" data-testid="history-filter-source">
                <SelectValue placeholder="All" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                {settings.sources?.map((src) => (
                  <SelectItem key={src} value={src}>{src}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-[10px] font-bold uppercase tracking-widest text-gray-500 mb-1.5 block">
              Service Advisor
            </Label>
            <Select
              value={filters.sa || "all"}
              onValueChange={(v) => updateFilter("sa", v)}
            >
              <SelectTrigger className="rounded-sm" data-testid="history-filter-sa">
                <SelectValue placeholder="All" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                {settings.service_advisors?.map((sa) => (
                  <SelectItem key={sa} value={sa}>{sa}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-[10px] font-bold uppercase tracking-widest text-gray-500 mb-1.5 block">
              Service Type
            </Label>
            <Select
              value={filters.service_type || "all"}
              onValueChange={(v) => updateFilter("service_type", v)}
            >
              <SelectTrigger className="rounded-sm" data-testid="history-filter-service-type">
                <SelectValue placeholder="All" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                {settings.service_types?.map((st) => (
                  <SelectItem key={st} value={st}>{st}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-[10px] font-bold uppercase tracking-widest text-gray-500 mb-1.5 block">
              Status
            </Label>
            <Select
              value={filters.status || "all"}
              onValueChange={(v) => updateFilter("status", v)}
            >
              <SelectTrigger className="rounded-sm" data-testid="history-filter-status">
                <SelectValue placeholder="All" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                {settings.appointment_statuses?.map((st) => (
                  <SelectItem key={st} value={st}>{st}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-4">
          <Button
            variant="outline"
            className="rounded-sm"
            onClick={() => {
              setFilters({ source: "", sa: "", service_type: "", status: "" });
            }}
            data-testid="history-filter-clear"
          >
            Clear All
          </Button>
          <Button
            className="bg-black text-white hover:bg-gray-800 rounded-sm"
            onClick={() => setFilterOpen(false)}
            data-testid="history-filter-apply"
          >
            Apply
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );

  return (
    <div className="space-y-6" data-testid="history-page">
      {/* Standard Header */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full space-y-6">
        <PageHeader
          icon={HistoryIcon}
          title="History"
          subtitle="Appointment History"
          onFilter={() => setFilterOpen(true)}
          filterTestId="history-filter-btn"
          onRefresh={handleRefresh}
          refreshTestId="history-refresh-btn"
          columnControls={{
            onCustomize: columnManager.openCustomize,
            onRearrange: () => columnManager.openRearrange(),
          }}
          leftSlot={
            <TabsList className="grid grid-cols-4 w-[32rem]">
          <TabsTrigger value="date" className="rounded-sm text-xs uppercase tracking-wider" data-testid="history-tab-date">Date</TabsTrigger>
          <TabsTrigger value="month" className="rounded-sm text-xs uppercase tracking-wider" data-testid="history-tab-month">Month</TabsTrigger>
          <TabsTrigger value="year" className="rounded-sm text-xs uppercase tracking-wider" data-testid="history-tab-year">Year</TabsTrigger>
          <TabsTrigger value="custom" className="rounded-sm text-xs uppercase tracking-wider" data-testid="history-tab-custom">Custom Range</TabsTrigger>
            </TabsList>
          }
        />

        {/* Date Tab */}
        <TabsContent value="date" className="mt-0 space-y-6">
          <div className="flex items-center justify-center gap-2">
            <Button variant="ghost" size="icon" onClick={handlePrevDate} className="h-10 w-10 rounded-sm" data-testid="date-prev-btn">
              <ChevronLeft className="w-5 h-5" strokeWidth={1.5} />
            </Button>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="min-w-[200px] rounded-sm text-sm" data-testid="date-picker-btn">
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {formatDateObj(selectedDate)}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 rounded-sm" align="center">
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={(date) => date && setSelectedDate(date)}
                  disabled={(date) => isAfter(date, yesterday)}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
            <Button variant="ghost" size="icon" onClick={handleNextDate} disabled={!canGoForwardDate} className="h-10 w-10 rounded-sm" data-testid="date-next-btn">
              <ChevronRight className="w-5 h-5" strokeWidth={1.5} />
            </Button>
          </div>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-2 border-black border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : (
            <AppointmentTable appointments={dateAppointments} />
          )}
        </TabsContent>

        {/* Month Tab */}
        <TabsContent value="month" className="mt-0 space-y-6">
          <div className="flex items-center justify-center gap-2">
            <Button variant="ghost" size="icon" onClick={handlePrevMonth} className="h-10 w-10 rounded-sm">
              <ChevronLeft className="w-5 h-5" strokeWidth={1.5} />
            </Button>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="min-w-[200px] rounded-sm text-sm">
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {format(selectedMonth, "MMMM yyyy")}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 rounded-sm" align="center">
                <Calendar
                  mode="single"
                  selected={selectedMonth}
                  onSelect={(date) => date && setSelectedMonth(startOfMonth(date))}
                  disabled={(date) => isAfter(startOfMonth(date), startOfMonth(today))}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
            <Button variant="ghost" size="icon" onClick={handleNextMonth} disabled={!canGoForwardMonth} className="h-10 w-10 rounded-sm">
              <ChevronRight className="w-5 h-5" strokeWidth={1.5} />
            </Button>
          </div>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-2 border-black border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : (
            <AppointmentTable appointments={monthAppointments} />
          )}
        </TabsContent>

        {/* Year Tab */}
        <TabsContent value="year" className="mt-0 space-y-6">
          <div className="flex items-center justify-center gap-2">
            <Button variant="ghost" size="icon" onClick={handlePrevYear} className="h-10 w-10 rounded-sm">
              <ChevronLeft className="w-5 h-5" strokeWidth={1.5} />
            </Button>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="min-w-[200px] rounded-sm text-sm">
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {format(selectedYear, "yyyy")}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 rounded-sm" align="center">
                <Calendar
                  mode="single"
                  selected={selectedYear}
                  onSelect={(date) => date && setSelectedYear(startOfYear(date))}
                  disabled={(date) => isAfter(startOfYear(date), startOfYear(today))}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
            <Button variant="ghost" size="icon" onClick={handleNextYear} disabled={!canGoForwardYear} className="h-10 w-10 rounded-sm">
              <ChevronRight className="w-5 h-5" strokeWidth={1.5} />
            </Button>
          </div>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-2 border-black border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : (
            <AppointmentTable appointments={yearAppointments} />
          )}
        </TabsContent>

        {/* Custom Range Tab */}
        <TabsContent value="custom" className="mt-0 space-y-6">
          <div className="flex items-center justify-center gap-2">
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="min-w-[200px] rounded-sm text-sm" data-testid="custom-from-btn">
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {fromDate ? formatDateObj(fromDate) : "From date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 rounded-sm" align="center">
                <Calendar mode="single" selected={fromDate} onSelect={setFromDate} disabled={(date) => isAfter(date, yesterday)} initialFocus />
              </PopoverContent>
            </Popover>
            <span className="text-gray-400 text-sm px-1">to</span>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="min-w-[200px] rounded-sm text-sm" data-testid="custom-to-btn">
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {toDate ? formatDateObj(toDate) : "To date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 rounded-sm" align="center">
                <Calendar mode="single" selected={toDate} onSelect={setToDate} disabled={(date) => isAfter(date, today) || (fromDate && date < fromDate)} initialFocus />
              </PopoverContent>
            </Popover>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-2 border-black border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : customRangeApplied ? (
            customRangeAppointments.length > 0 ? (
              <AppointmentTable appointments={customRangeAppointments} />
            ) : (
              <Card className="border border-gray-200 rounded-sm shadow-none p-12 text-center">
                <p className="text-gray-400">No data found, select different dates and click on Apply</p>
              </Card>
            )
          ) : (
            <Card className="border border-gray-200 rounded-sm shadow-none p-12 text-center">
              <p className="text-gray-400">Select date range and click Apply to view appointments</p>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Filter Dialog */}
      <FilterDialog />

      {/* Customize + Rearrange modals (shared) */}
      <ColumnModals
        columnManager={columnManager}
        defaultColumns={HISTORY_COLUMNS}
        customizableIds={CUSTOMIZABLE_APPOINTMENT_COLUMN_IDS}
      />
    </div>
  );
};

export default History;
